'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useUser } from '@clerk/nextjs';
import { getApiBase } from '@/lib/apiBase';

type SortBy = 'alphabetical' | 'alphabeticalByName' | 'lastUpdated';

interface SortContextType {
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
}

const SortContext = createContext<SortContextType | undefined>(undefined);

export function SortProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const [sortBy, setSortByState] = useState<SortBy>('lastUpdated');
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load preference from API when user signs in
  useEffect(() => {
    if (!isSignedIn) {
      setHasLoadedPreference(false);
      return;
    }

    const loadPreference = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.preferences?.sortBy) {
            setSortByState(data.preferences.sortBy);
          }
        }
      } catch (error) {
        console.error('Failed to load sort preference:', error);
      } finally {
        setHasLoadedPreference(true);
      }
    };

    loadPreference();
  }, [isSignedIn]);

  // Save preference to API (debounced)
  const savePreference = useCallback(async (newSortBy: SortBy) => {
    if (!isSignedIn) return;

    try {
      await fetch(`${getApiBase()}/api/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sortBy: newSortBy }),
      });
    } catch (error) {
      console.error('Failed to save sort preference:', error);
    }
  }, [isSignedIn]);

  // Wrapper that updates state and persists to API
  const setSortBy = useCallback((newSortBy: SortBy) => {
    setSortByState(newSortBy);

    // Only save to API if signed in and initial load is complete
    if (isSignedIn && hasLoadedPreference) {
      // Debounce the save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        savePreference(newSortBy);
      }, 300);
    }
  }, [isSignedIn, hasLoadedPreference, savePreference]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <SortContext.Provider value={{ sortBy, setSortBy }}>
      {children}
    </SortContext.Provider>
  );
}

export function useSortBy() {
  const context = useContext(SortContext);
  if (context === undefined) {
    throw new Error('useSortBy must be used within a SortProvider');
  }
  return context;
}
